// Hand-rolled inline-SVG charts for the analytics dashboard
// (analytics+postgres Phase 6). Zero new dependencies: Loom already ships
// inline-SVG sparklines + icons and has no chart library, and a line + a
// split-bar on a maintainer-only page at tens of users does not justify
// recharts/visx (~50KB + D3). Everything here is a pure function of its props.

import type { JSX } from "react";

// ---------------------------------------------------------------------------
// LineChart — daily timeseries. A suppressed/absent day is a gap, not a zero:
// we break the polyline rather than draw a misleading dip to the axis.
// ---------------------------------------------------------------------------

export interface LinePoint {
  readonly label: string;
  /** null = suppressed/absent → rendered as a gap. */
  readonly value: number | null;
}

interface LineChartProps {
  readonly points: ReadonlyArray<LinePoint>;
  readonly width?: number;
  readonly height?: number;
  readonly ariaLabel: string;
}

export const LineChart = ({
  points,
  width = 640,
  height = 140,
  ariaLabel,
}: LineChartProps): JSX.Element => {
  const padX = 6;
  const padY = 10;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  const values = points.map((p) => p.value).filter((v): v is number => v != null);
  const max = values.length ? Math.max(...values, 1) : 1;
  const n = points.length;

  const x = (i: number): number => (n <= 1 ? padX : padX + (innerW * i) / (n - 1));
  const y = (v: number): number => padY + innerH - (innerH * v) / max;

  // Split into contiguous non-null segments so gaps break the line.
  const segments: Array<Array<{ x: number; y: number }>> = [];
  let current: Array<{ x: number; y: number }> = [];
  points.forEach((p, i) => {
    if (p.value == null) {
      if (current.length) segments.push(current);
      current = [];
      return;
    }
    current.push({ x: x(i), y: y(p.value) });
  });
  if (current.length) segments.push(current);

  return (
    <svg
      className="analytics-chart"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel}
    >
      {/* Baseline */}
      <line
        x1={padX}
        y1={padY + innerH}
        x2={padX + innerW}
        y2={padY + innerH}
        stroke="var(--border)"
        strokeWidth="1"
      />
      {segments.map((seg, si) => (
        <polyline
          key={si}
          points={seg.map((pt) => `${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(" ")}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {/* End-point dots make a single-day or sparse series visible. */}
      {segments.flatMap((seg, si) =>
        seg.map((pt, pi) => (
          <circle key={`${si}-${pi}`} cx={pt.x} cy={pt.y} r="2.2" fill="var(--accent)" />
        )),
      )}
    </svg>
  );
};

// ---------------------------------------------------------------------------
// ModeBar — a single horizontal split bar for the PM / Coding session mix.
// Sessions can use both modes, so the two counts are NOT a partition; the bar
// shows each as a share of the larger of (pm+coding, total) to stay honest.
// ---------------------------------------------------------------------------

interface ModeBarProps {
  readonly pm: number;
  readonly coding: number;
  readonly pmLabel: string;
  readonly codingLabel: string;
}

export const ModeBar = ({ pm, coding, pmLabel, codingLabel }: ModeBarProps): JSX.Element => {
  const total = Math.max(pm + coding, 1);
  const pmPct = (pm / total) * 100;
  const codingPct = (coding / total) * 100;
  return (
    <div className="analytics-modebar" role="img" aria-label={`${pmLabel} ${pm}, ${codingLabel} ${coding}`}>
      <div className="analytics-modebar-track">
        <span className="analytics-modebar-seg analytics-modebar-pm" style={{ width: `${pmPct}%` }} />
        <span className="analytics-modebar-seg analytics-modebar-coding" style={{ width: `${codingPct}%` }} />
      </div>
      <div className="analytics-modebar-legend">
        <span>
          <i className="analytics-dot analytics-dot-pm" /> {pmLabel} {pm}
        </span>
        <span>
          <i className="analytics-dot analytics-dot-coding" /> {codingLabel} {coding}
        </span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CostSplitBar — the four metered surfaces of total spend as one labeled split
// bar (analytics+postgres Phase 9). Same hand-rolled approach as ModeBar; these
// four components ARE a partition of the total, so the bar shares 100% over their
// sum. `amount` is the raw 6-dp USD string (used only for the proportion); the
// caller passes the already-formatted `display` string so we never hand-round.
// ---------------------------------------------------------------------------

export interface CostSplitSegment {
  /** Stable key + dot color class suffix: coding | pm | handoff | workflow. */
  readonly surface: "coding" | "pm" | "handoff" | "workflow";
  readonly label: string;
  /** Raw 6-dp USD string — proportion math only. */
  readonly amount: string;
  /** Pre-formatted currency display string. */
  readonly display: string;
}

interface CostSplitBarProps {
  readonly segments: ReadonlyArray<CostSplitSegment>;
}

export const CostSplitBar = ({ segments }: CostSplitBarProps): JSX.Element => {
  const sized = segments.map((s) => ({ ...s, amount: Math.max(Number(s.amount), 0) }));
  const total = Math.max(
    sized.reduce((sum, s) => sum + s.amount, 0),
    Number.EPSILON,
  );
  const ariaLabel = segments.map((s) => `${s.label} ${s.display}`).join(", ");
  return (
    <div className="analytics-costbar" role="img" aria-label={ariaLabel}>
      <div className="analytics-costbar-track">
        {sized.map((s) => (
          <span
            key={s.surface}
            className={`analytics-costbar-seg analytics-costbar-${s.surface}`}
            style={{ width: `${(s.amount / total) * 100}%` }}
          />
        ))}
      </div>
      <div className="analytics-costbar-legend">
        {segments.map((s) => (
          <span key={s.surface}>
            <i className={`analytics-dot analytics-dot-${s.surface}`} /> {s.label}{" "}
            <span className="analytics-costbar-amount">{s.display}</span>
          </span>
        ))}
      </div>
    </div>
  );
};
